import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, send } from '../../api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useAgentName } from '../../agent-name';
import type { InstructionsView } from '../../../../src/application/carol/brief';
import type { CarolAction, CarolPermissions, PermissionMode } from '../../../../src/domain/carol/permissions';
import './carol-instructions.css';

const actionNames: Record<CarolAction, string> = {
  reminder_email: 'Reminder and follow-up emails', reply_answer: 'Answers to replies', documents: 'Sending documents', call: 'Calls',
  promise_record: 'Recording promises', dispute_record: 'Recording disputes', note: 'Notes', ask_person: 'Asking a person',
};
const actions = Object.keys(actionNames) as CarolAction[];
const modeNames: Record<PermissionMode, string> = { off: 'Off', draft: 'A person approves', ladder: 'Earned autonomy', auto: 'On her own' };
const modes: PermissionMode[] = ['off', 'draft', 'ladder', 'auto'];
const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const words = (text: string) => text.trim() ? text.trim().split(/\s+/).length : 0;
const blank: CarolPermissions = {
  version: 1, channels: { email: true, call: true, documents: true },
  actions: { reminder_email: 'ladder', reply_answer: 'ladder', documents: 'ladder', call: 'draft', promise_record: 'draft',
    dispute_record: 'draft', note: 'auto', ask_person: 'auto' },
  limits: { touchesPerWeek: null, minDaysBetweenTouches: null, callWindow: null, noCallCustomers: [] },
};
const numberOrNull = (value: string) => value.trim() === '' ? null : Math.max(0, Math.trunc(Number(value)));

/**
 * "Carol's instructions" on the Policy page (#326): the owner's brief to the agent in prose, its version history with the
 * diff from the previous version, and her structured permissions as toggles. The prose is guidance for the model; the
 * permissions are enforced by code. Owners edit; everyone else reads.
 */
export function CarolInstructions({ owner }: { owner: boolean }) {
  const agent = useAgentName();
  const [view, setView] = useState<InstructionsView | null>(null);
  const [draft, setDraft] = useState('');
  const [perms, setPerms] = useState<CarolPermissions>(blank);
  const [busy, setBusy] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const apply = useCallback((next: InstructionsView) => {
    setView(next);
    setDraft(next.instructions?.body ?? next.template);
    setPerms(next.permissions?.value ?? blank);
  }, []);
  useEffect(() => { void api<InstructionsView>('/app-api/carol/instructions').then(apply).catch(() => setView(null)); }, [apply]);
  const count = useMemo(() => words(draft), [draft]);
  if (!view) return null;
  const tooLong = count > view.maxWords;
  async function run(work: () => Promise<InstructionsView>, done: string) {
    if (busy) return;
    setBusy(true);
    try { apply(await work()); toast.success(done); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'This could not be saved.'); }
    finally { setBusy(false); }
  }
  const saveText = () => run(() => send<InstructionsView>('/app-api/carol/instructions', 'PUT', { body: draft }), 'Instructions saved as a new version.');
  const savePerms = () => run(() => send<InstructionsView>('/app-api/carol/permissions', 'PUT', perms), 'Permissions saved as a new version.');
  const loadPreset = (key: keyof InstructionsView['presets']) => { const p = view.presets[key]; setDraft(p.instructions); setPerms(p.permissions); };
  const setMode = (action: CarolAction, mode: PermissionMode) => setPerms(p => ({ ...p, actions: { ...p.actions, [action]: mode } }));
  const callWindow = perms.limits.callWindow;
  const setLimits = (limits: Partial<CarolPermissions['limits']>) => setPerms(p => ({ ...p, limits: { ...p.limits, ...limits } }));
  return <section className="carol-instructions" aria-labelledby="carol-instructions-title">
    <div className="ladder-head"><h3 id="carol-instructions-title">{agent}'s instructions</h3>
      {owner && <div className="ci-presets">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => loadPreset('aiprise')}>{view.presets.aiprise.label}</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => loadPreset('cashline')}>{view.presets.cashline.label}</Button>
      </div>}</div>
    {!view.enabled && <p className="ladder-line">{agent} is not using these instructions yet: saving keeps a version, and {agent}
      reads it once they are switched on.</p>}
    <p className="ladder-line">Brief {agent} the way you would brief a new hire. This is guidance: it can never override the hard rules
      (ledger facts, contacts on file, bank details, money). What she may do is set by the permissions below, which code enforces.</p>
    <label className="ci-label" htmlFor="carol-instructions-body">Instructions
      {view.instructions ? ` (version ${view.instructions.version}, by ${view.instructions.author})` : ' (not saved yet: starter template)'}</label>
    <Textarea id="carol-instructions-body" className="ci-body" value={draft} readOnly={!owner} aria-invalid={tooLong || undefined}
      onChange={e => setDraft(e.target.value)} rows={14}/>
    <div className="ci-row">
      <span className={tooLong ? 'ci-count ci-over' : 'ci-count'}>{count.toLocaleString()} of about {view.maxWords.toLocaleString()} words</span>
      {owner && <Button size="sm" disabled={busy || tooLong || !draft.trim() || draft === view.instructions?.body} onClick={() => void saveText()}>
        Save instructions</Button>}
    </div>
    {view.history.length > 0 && <details className="ci-history" open={showDiff} onToggle={e => setShowDiff((e.target as HTMLDetailsElement).open)}>
      <summary>Version history ({view.history.length})</summary>
      <ol>{view.history.map(v => <li key={v.id}><strong>v{v.version}</strong> <span>{new Date(v.createdAt).toLocaleString()}</span>
        <span>{v.author}</span><span>{v.words} words</span>{v.active && <span className="ci-active">active</span>}</li>)}</ol>
      {view.diff.length > 0 && <pre className="ci-diff" aria-label="Changes from the previous version">{view.diff.map((line, i) =>
        <span key={i} data-kind={line.kind}>{line.kind === 'added' ? '+ ' : line.kind === 'removed' ? '- ' : '  '}{line.text}{'\n'}</span>)}</pre>}
    </details>}

    <h3 className="ci-sub">What {agent} may do</h3>
    {view.permissionsInvalid && <div className="alert error" role="alert">The stored permissions could not be read, so everything waits
      for a person. Save them again.</div>}
    <fieldset className="ci-channels" disabled={!owner}><legend>Channels</legend>
      {(['email', 'call', 'documents'] as const).map(channel => <label key={channel}>
        <input type="checkbox" checked={perms.channels[channel]}
          onChange={e => setPerms(p => ({ ...p, channels: { ...p.channels, [channel]: e.target.checked } }))}/>
        {channel === 'email' ? 'Email' : channel === 'call' ? 'Calls' : 'Documents'}</label>)}
    </fieldset>
    <table className="ladder-table ci-table">
      <thead><tr><th scope="col">Action</th>{modes.map(m => <th key={m} scope="col">{modeNames[m]}</th>)}</tr></thead>
      <tbody>{actions.map(action => <tr key={action}><th scope="row">{actionNames[action]}</th>
        {modes.map(mode => <td key={mode}><input type="radio" name={`perm-${action}`} aria-label={`${actionNames[action]}: ${modeNames[mode]}`}
          checked={perms.actions[action] === mode} disabled={!owner || (action === 'ask_person' && mode === 'off')}
          onChange={() => setMode(action, mode)}/></td>)}</tr>)}</tbody>
    </table>
    <fieldset className="ci-limits" disabled={!owner}><legend>Limits</legend>
      <label>Touches per customer per week <input type="number" min={1} max={50} value={perms.limits.touchesPerWeek ?? ''}
        placeholder="policy" onChange={e => setLimits({ touchesPerWeek: numberOrNull(e.target.value) })}/></label>
      <label>Fewest days between touches <input type="number" min={0} max={30} value={perms.limits.minDaysBetweenTouches ?? ''}
        placeholder="default" onChange={e => setLimits({ minDaysBetweenTouches: numberOrNull(e.target.value) })}/></label>
      <label><input type="checkbox" checked={callWindow !== null}
        onChange={e => setLimits({ callWindow: e.target.checked ? { days: [1, 2, 3, 4, 5], start: '09:00', end: '17:00' } : null })}/>
        Calling hours</label>
      {callWindow && <div className="ci-window">
        {dayNames.map((name, i) => <label key={name}><input type="checkbox" checked={callWindow.days.includes(i + 1)}
          onChange={e => setLimits({ callWindow: { ...callWindow, days: e.target.checked ? [...callWindow.days, i + 1].sort((a, b) => a - b)
            : callWindow.days.filter(d => d !== i + 1) } })}/>{name}</label>)}
        <label>From <input type="time" value={callWindow.start} onChange={e => setLimits({ callWindow: { ...callWindow, start: e.target.value } })}/></label>
        <label>to <input type="time" value={callWindow.end} onChange={e => setLimits({ callWindow: { ...callWindow, end: e.target.value } })}/></label>
      </div>}
      <label>Never call (customer ids, comma-separated) <input type="text" value={perms.limits.noCallCustomers.join(', ')}
        onChange={e => setLimits({ noCallCustomers: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}/></label>
    </fieldset>
    <fieldset className="ci-channels" disabled={!owner}><legend>New senders</legend>
      <label><input type="checkbox" checked={perms.trust_verified_domain_senders === true}
        onChange={e => setPerms(p => ({ ...p, trust_verified_domain_senders: e.target.checked }))}/>
        Add a verified sender at a customer’s own domain as a contact without asking (never for public or shared domains, or a
        bank change)</label>
    </fieldset>
    {view.permissionsText && <details className="ci-history"><summary>What {agent} reads (version {view.permissions?.version})</summary>
      <pre className="ci-diff">{view.permissionsText}</pre></details>}
    {owner && <div className="ci-row"><span className="ci-count">{view.permissions ? `Version ${view.permissions.version}, by ${view.permissions.author}` : 'Not set: the policy alone decides'}</span>
      <Button size="sm" disabled={busy} onClick={() => void savePerms()}>Save permissions</Button></div>}
  </section>;
}
